import { useMutation } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";

export function usePaymentMutations({ queryClient, sales, onPaymentFormClose }) {
  const createPaymentMutation = useMutation({
    mutationFn: async (data) => {
      const payment = await base44.entities.Payment.create(data);
      const sale = sales.find((s) => s.id === data.sale_id);
      if (sale) {
        const newTotalPaid = (sale.total_paid || 0) + data.amount;
        const totalPrice = sale.total_price || 0;
        const updateData = { total_paid: newTotalPaid };
        if (newTotalPaid >= totalPrice) updateData.status = "completed";
        await base44.entities.Sale.update(sale.id, updateData);
      }
      return payment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      onPaymentFormClose?.();
      if (window.addNotification) window.addNotification("Payment recorded successfully", "success");
    },
    onError: (error) => {
      if (window.addNotification) window.addNotification(error?.message || "Failed to record payment", "error");
      else alert(error?.message || "Failed to record payment");
    },
  });

  const updatePaymentMutation = useMutation({
    mutationFn: async ({ id, data, oldAmount }) => {
      await base44.entities.Payment.update(id, data);
      const sale = sales.find((s) => s.id === data.sale_id);
      if (sale && oldAmount !== data.amount) {
        const adjustment = data.amount - oldAmount;
        const newTotalPaid = (sale.total_paid || 0) + adjustment;
        const totalPrice = sale.total_price || 0;
        const updateData = { total_paid: newTotalPaid };
        if (newTotalPaid >= totalPrice) updateData.status = "completed";
        else if (sale.status === "completed") updateData.status = "active";
        await base44.entities.Sale.update(sale.id, updateData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      onPaymentFormClose?.();
      if (window.addNotification) window.addNotification("Payment updated successfully", "success");
    },
    onError: (error) => {
      if (window.addNotification) window.addNotification(error?.message || "Failed to update payment", "error");
      else alert(error?.message || "Failed to update payment");
    },
  });

  const deletePaymentMutation = useMutation({
    mutationFn: (payment) => base44.entities.Payment.delete(payment.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
    },
  });

  return { createPaymentMutation, updatePaymentMutation, deletePaymentMutation };
}